import { Telemetry } from '@amzn/ihm-telemetry-js-lib';
import { State } from '../state';
import { Persistence, Operation } from '../persistence';
import { Event } from './_event';
import { Action } from './action';

export class Task extends Event {
    private actionIds: string[];

    protected save() {
        Persistence.save(Operation.TASK, this._id, this.toJSON());
    }

    protected static getCurrentTask() {
        const taskId = Persistence.getCurrent(Operation.TASK);
        return Task.fromJSON(Persistence.getById(Operation.TASK, taskId));
    }

    protected setCurrent() {
        Persistence._setCurrent(Operation.TASK, this._id);
    }

    protected delete() {
        Persistence.delete(Operation.TASK, this._id);
    }

    protected hydrate(data: any) {
        super.hydrate(data);
        this.actionIds = Array.isArray(data.actionIds) ? data.actionIds : [];
    }

    private getChildren() {
        try {
            const actions = Persistence.getAll(Operation.ACTION);
            return this.actionIds.map((id) => Action.fromJSON(actions[id])).filter(Boolean);
        } catch (error) {
            console.error('Error getting child actions:', error);
            return [];
        }
    }

    private getCurrentWorkflowId(): string | null {
        try {
            const currentWorkflowId = Persistence.getCurrent(Operation.WORKFLOW);
            if (!currentWorkflowId) return null;

            const workflowData = Persistence.getById(Operation.WORKFLOW, currentWorkflowId);
            return workflowData?.eventId || null;
        } catch (error) {
            console.error('Error getting current workflow ID:', error);
            return null;
        }
    }

    public constructor(name: string, save: boolean = true) {
        super(name);
        this.actionIds = [];
        if (save) this.save();
    }

    public static fromJSON(data: any) {
        if (!data) return null;
        const task = new Task(data.name, false);
        task.hydrate(data);
        task.save();
        return task;
    }

    public toJSON() {
        return {
            ...super.toJSON(),
            actionIds: this.actionIds,
        };
    }

    public start() {
        try {
            super.start();
            Task.getCurrentTask()?.stop(true);
            this.setCurrent();
            this.save();
            console.debug('[Telemetry] Task started successfully with the name: ' + this.name + ', id: ' + this._id);
        } catch (error) {
            console.error('Error starting action:', error);
        }
    }

    public stop(aborted: boolean = false) {
        try {
            this.getChildren().forEach((action) => action.stop(true));
            if (!this.startTime || this.completed) return;
            super.stop(aborted);
            this.save();
            const data = Event.getPayload(Persistence.getById(Operation.TASK, this._id));
            if (data) {
                data.workflowId = this.getCurrentWorkflowId();
                data.aborted = aborted.toString();
                if (!aborted) {
                    Telemetry.emit('process.task', data);
                } else {
                    const currentToolId = State.getWorkflow()?.workflowName ?? 'unknown';
                    console.debug('Aborted task' + this.name + 'emitted by' + currentToolId);
                    Telemetry.emit('process.taskAborted', data);
                }
            }
            console.info(
                '[Telemetry] Task stopped successfully with the name: ' + this.name + ', id: ' + this._id + ', aborted: ' + aborted.toString() + ', duration: ' + this.duration,
            );
            this.delete();
        } catch (error) {
            console.error('Error stopping task:', error);
        }
    }

    public addAction(name: string) {
        const action = new Action(name, this.name);
        this.actionIds.push(action.id);
        this.save();
        return action;
    }
}
